import { supabase } from '../lib/supabase';

// Convert database snake_case to React camelCase
const convertToEmployeeObject = (dbEmployee) => {
  if (!dbEmployee) return null;
  
  return {
    id: dbEmployee?.id,
    nombre: dbEmployee?.nombre,
    rol: dbEmployee?.rol,
    activo: dbEmployee?.activo,
    createdAt: dbEmployee?.created_at,
    authId: dbEmployee?.auth_id
  };
};

export const employeeService = {
  // Get all employees
  async getAll() {
    const { data, error } = await supabase?.from('empleados')?.select('*')?.order('nombre', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(convertToEmployeeObject);
  },

  // Get single employee by ID
  async getById(id) {
    const { data, error } = await supabase?.from('empleados')?.select('*')?.eq('id', id)?.single();
    
    if (error) throw error;
    
    return convertToEmployeeObject(data);
  },

  // Create new employee
  async create(employeeData) {
    const { data, error } = await supabase?.from('empleados')?.insert({
        nombre: employeeData?.nombre,
        rol: employeeData?.rol,
        activo: employeeData?.activo !== undefined ? employeeData?.activo : true
      })?.select()?.single();
    
    if (error) throw error;
    
    return convertToEmployeeObject(data);
  },

  // Update existing employee
  async update(id, employeeData) {
    const updateData = {
      nombre: employeeData?.nombre,
      rol: employeeData?.rol
    };
    
    // Only include activo if it's explicitly provided
    if (employeeData?.activo !== undefined) {
      updateData.activo = employeeData?.activo;
    }
    
    const { data, error } = await supabase?.from('empleados')?.update(updateData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    
    return convertToEmployeeObject(data);
  },

  // Delete employee
  async delete(id) {
    const { error } = await supabase?.from('empleados')?.delete()?.eq('id', id);
    
    if (error) throw error;
  },

  // Toggle employee active status
  async toggleActive(id, currentStatus) {
    const { data, error } = await supabase?.from('empleados')?.update({ activo: !currentStatus })?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    
    return convertToEmployeeObject(data);
  },

  // Get top performers with pieces count and labor costs for current week
  async getTopPerformers() {
    // Get current week start (Monday) and end (Sunday)
    const now = new Date();
    const dayOfWeek = now?.getDay();
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(now);
    monday?.setDate(now?.getDate() + diffToMonday);
    monday?.setHours(0, 0, 0, 0);
    
    const sunday = new Date(monday);
    sunday?.setDate(monday?.getDate() + 6);
    sunday?.setHours(23, 59, 59, 999);

    // Fetch all employees
    const { data: employees, error: employeesError } = await supabase
      ?.from('empleados')
      ?.select('id, nombre')
      ?.eq('activo', true);
    
    if (employeesError) throw employeesError;
    if (!employees || employees?.length === 0) return [];

    // Fetch pieces count per employee for current week
    const { data: loncheadosData, error: loncheadosError } = await supabase
      ?.from('loncheados')
      ?.select('empleado_id')
      ?.gte('created_at', monday?.toISOString())
      ?.lte('created_at', sunday?.toISOString());
    
    if (loncheadosError) throw loncheadosError;

    // Fetch labor costs per employee for current week
    const { data: salariosData, error: salariosError } = await supabase
      ?.from('salarios')
      ?.select('empleado_id, importe')
      ?.gte('created_at', monday?.toISOString())
      ?.lte('created_at', sunday?.toISOString());
    
    if (salariosError) throw salariosError;

    // Calculate pieces and costs per employee
    const employeeStats = employees?.map(employee => {
      const pieces = loncheadosData?.filter(l => l?.empleado_id === employee?.id)?.length || 0;
      const earnings = salariosData
        ?.filter(s => s?.empleado_id === employee?.id)
        ?.reduce((sum, s) => sum + (parseFloat(s?.importe) || 0), 0) || 0;
      
      return {
        id: employee?.id,
        name: employee?.nombre,
        pieces,
        earnings,
        avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_16be51f71-1763295001188.png",
        avatarAlt: `Retrato profesional de ${employee?.nombre}`,
        growth: 0 // Growth calculation would require historical data
      };
    });

    // Sort by pieces descending and return
    return employeeStats?.sort((a, b) => b?.pieces - a?.pieces);
  }
};